import "../../util/mobile-table.css";
import {
  notification,
  Button,
  Table,
  Space,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { ConfiguratorContext, S3UrlMapContext} from "../../context";
import { TruckComment, CommentTopic } from "../../api/models"
import _ from "lodash";
import { useIntl } from "react-intl";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import BmMentions from "../BmMentions";
import Utils from "../../util/util";
import useUsers from "../../swr/useUsers";
import TruckCommentCard, {EditTruckCommentCard} from "../TruckCommentCard";
import useTruckComments from "../../swr/useTruckComments";
import useTruckCommentDocumentUrls from "../../swr/useTruckCommentDocumentUrls";

export const getDefaultCommentTopics = (isAdminOrEngineering?:boolean) => {
  return ( isAdminOrEngineering )
      ? [CommentTopic.UserComment, CommentTopic.SystemActivity ]
      : [CommentTopic.UserComment, CommentTopic.SystemActivity ];
}

const TruckCommentList = (props: {
  title?: string
  serialNumber: string | undefined;
  topics?: CommentTopic[]
  tags?:string[] | undefined
  hideSearch?:boolean
  adminView?:boolean
}) => {

  const configurator = useContext(ConfiguratorContext);
  const isAdminOrEngineering = configurator.isAdmin() || configurator.isEngineering();

  const DefaultTopics = props.topics || getDefaultCommentTopics(isAdminOrEngineering);

  const intl = useIntl();
  const [editCommentId, setEditCommentId] = useState<number | undefined>();
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showAddComment, setShowAddComment] = useState<boolean>(false);
  const [search, setSearch] = useState<string | undefined>();

  const userLst = useUsers().data;

  useEffect(() => {
    setSearch(props.tags?.join(" "));
  }, [props.tags] );

  const commentOptions = {
    topic: DefaultTopics,
    showHidden: props.adminView,
  };

  const truckComments = useTruckComments({
    serialNumber: props?.serialNumber,
    options: commentOptions,
  })

  const s3UrlMap = useTruckCommentDocumentUrls({
    serialNumber: props?.serialNumber,
    options: commentOptions,
  })

  useEffect(() => {
    const serialNumber = props?.serialNumber;
    if ( serialNumber?.length ) {
      Promise.all( truckComments.data?.map( c => {
        updateTruckCommentLastViewed(serialNumber, c.id);
      } ) || [] );
    }
  }, [props?.serialNumber, commentOptions?.showHidden, truckComments.isLoading] );

  const updateTruckCommentLastViewed = async (serialNumber:string, commentId:number) : Promise<TruckComment | undefined> => {
    if ( !serialNumber ) return;

    try {
      const resp = await configurator.api.updateCommentLastViewed(commentId);
      return resp.data;
    } catch (e:any) {
      const errorMsg = intl.formatMessage({ id: e.message });
      notification.error( { message: "Failed to update quote comment last viewed. " + errorMsg });
    }
    return;
  }

  const handleShowAddComment = () => {
    setEditCommentId(undefined);
    setShowAddComment(true);
  }

  const handleEditComment = (commentId:number) => {
    setEditCommentId(commentId);
    setShowAddComment(false);
  }


  const handleAddedComment = (comment:TruckComment) => {
    truckComments.mutate();
    setShowAddComment(false);
  }

  const handleEditedComment = (comment:TruckComment) => {
    truckComments.mutate();
    setEditCommentId(undefined);
  }

  const isAddCommentsVisible = ( commentOptions?.topic?.includes( CommentTopic.UserComment ) && !showAddComment );

  const datasource = truckComments.data
    ?.filter( c => {
      if ( !search ) return true;
      const searchFilter = Utils.splitByWhitespacePreservingQuotes(search);
      const searchTxt = [c.text].concat(c.tags?.flat()).join(" ");
      return Utils.searchValue( searchFilter, searchTxt );
    })
    .sort((a,b) => dayjs(a.createdAt).isAfter(b.createdAt) ? -1 : 1);
  const pageSize = 7;
  const pagination = ((datasource?.length || 0) > pageSize) ? { pageSize } : false; 

  return <>
    <S3UrlMapContext.Provider value={{s3UrlMap: s3UrlMap.data}}>

      <Space direction="vertical" style={{width: "100%"}}>

        <div style={{display: "flex", justifyContent: "space-between", alignItems: "baseline", }}>
          {!!props.title?.length &&
          <div style={{fontSize: "16px", fontWeight: "600"}}>{props.title}</div>}
        <div style={{display: "flex", flexDirection: "row-reverse", alignItems: "baseline", gap: ".2rem"}}>
          <div>
            {!showAddComment && isAddCommentsVisible  &&
                <Button icon={<PlusOutlined />} shape="circle" type="primary" size="small"
                        onClick={handleShowAddComment}
                />}
          </div>
          <div>
            <Button icon={<SearchOutlined />} shape="circle" type="primary" size="small"
                    onClick={() => setShowSearch(!showSearch)}
            />
          </div>
        </div>
        </div>

        {(!props.hideSearch && showSearch && !showAddComment) &&
            <div style={{margin: "0px 0px 0px 8px", padding: "0px 8px 0px 0px"}} >
              <BmMentions
                  allowClear
                  rows={1}
                  value={search}
                  placeholder="Search Comments"
                  onChange={(txt) => setSearch( txt)}
                  mentions={{
                    '@': userLst?.map( u => ({value: _.upperFirst(_.camelCase( u.name)), label: u.name })),
                    '#': props.tags?.map( t => ({value: _.upperFirst(_.camelCase( t )), label: t }))
                  }}
              />
            </div> }

      {showAddComment &&
      <EditTruckCommentCard
        serialNumber={props.serialNumber}
        comment={{
          topic: CommentTopic.UserComment
        }}
        reset={showAddComment}
        onCancel={() => setShowAddComment(false)}
        onSave={handleAddedComment}
      />}

      <Table
        className="mobile-table"
        size="small"
        bordered={false}
        rowKey="id"
        loading={truckComments.isLoading}
        dataSource={datasource}
        pagination={pagination}
        columns={[{ render: (comment) => 
          (editCommentId === comment.id ) 
            ? <EditTruckCommentCard comment={comment}
              serialNumber={props.serialNumber}
              onCancel={() => setEditCommentId(undefined)}
              onSave={handleEditedComment}
            /> 
            : <TruckCommentCard comment={comment}
              onEdit={handleEditComment} 
            />
        }]}
      />
      </Space>

    </S3UrlMapContext.Provider>
  </>
}

export default TruckCommentList;


